/**
 * Verification Dialog Helper Functions
 *
 * Utility functions for the verification dialog component including
 * date calculations, unit counting, and data transformations.
 */

import moment from 'moment';
import {
  TREATMENT_TYPES,
  TREATMENT_STATUS_IDS,
  COUNTABLE_STATUS_IDS,
  TREATMENT_FIELDS,
} from './verificationConstants';
import { formDateFormat } from '../../../constants';

/**
 * Calculate date for a specific day of the week
 * @param {Date|string} monday - The Monday date of the week
 * @param {number} dayOffset - Number of days to add (0 = Monday, 6 = Sunday)
 * @returns {Date} The calculated date
 */
export const calculateDayDate = (monday, dayOffset) => {
  return new Date(new Date(monday).setDate(new Date(monday).getDate() + dayOffset));
};

/**
 * Get all dates for the week starting from Monday
 * @param {Date|string} monday - The Monday date of the week
 * @returns {Object} Object with day names as keys and dates as values
 */
export const getWeekDates = (monday) => {
  return {
    monday: calculateDayDate(monday, 0),
    tuesday: calculateDayDate(monday, 1),
    wednesday: calculateDayDate(monday, 2),
    thursday: calculateDayDate(monday, 3),
    friday: calculateDayDate(monday, 4),
    saturday: calculateDayDate(monday, 5),
    sunday: calculateDayDate(monday, 6),
  };
};

/**
 * Count total units for the week based on treatment status IDs
 * @param {Object} verificationState - The verification state object
 * @param {string} treatmentType - Type of treatment (INDIVIDUAL or GROUP)
 * @returns {number} Total count of units
 */
export const calculateUnitCounts = (verificationState, treatmentType) => {
  let count = 0;

  TREATMENT_FIELDS.forEach((field) => {
    const statusId = verificationState[field.field];

    // Only count if status ID is in countable list
    if (statusId && COUNTABLE_STATUS_IDS.includes(parseInt(statusId))) {
      // Count all for total, or filter by type for individual/group
      if (!treatmentType || field.type === treatmentType) {
        count++;
      }
    }
  });

  return count;
};

/**
 * Check if a treatment status counts towards unit limits
 * @param {number|string} statusId - Treatment status ID
 * @returns {boolean} True if status counts towards units
 */
export const isCountableStatus = (statusId) => {
  return COUNTABLE_STATUS_IDS.includes(parseInt(statusId));
};

/**
 * Get treatment field configuration by field name
 * @param {string} fieldName - Field name (e.g., 'i1TmtTmtStsId')
 * @returns {Object|undefined} Field configuration object or undefined
 */
export const getTreatmentFieldConfig = (fieldName) => {
  return TREATMENT_FIELDS.find((field) => field.field === fieldName);
};

/**
 * Format time display from hours and minutes
 * @param {number|string} hours - Hours value
 * @param {number|string} mins - Minutes value
 * @returns {string} Formatted time string (HH:MM)
 */
export const formatTimeDisplay = (hours, mins) => {
  const h = hours || '0';
  const m = mins || '0';
  return `${h}:${m}`;
};

/**
 * Parse time string into hours and minutes
 * @param {string} timeString - Time string in format HH:MM
 * @returns {Object} Object with hours and mins properties
 */
export const parseTimeString = (timeString) => {
  if (!timeString || typeof timeString !== 'string') {
    return { hours: '0', mins: '0' };
  }

  const parts = timeString.split(':');
  return {
    hours: parts[0] || '0',
    mins: parts[1] || '0',
  };
};

/**
 * Check if a day should be disabled based on prior discharge
 * @param {number} dayIndex - Index of the day (0-6)
 * @param {Object} verificationState - The verification state object
 * @returns {boolean} True if day should be disabled
 */
export const isDayDisabled = (dayIndex, verificationState) => {
  // Check if any previous day has a discharge
  for (let i = 0; i < dayIndex; i++) {
    const dischargeField = `d${i + 1}TmtTmtStsId`;
    if (verificationState[dischargeField]) {
      return true;
    }
  }
  return false;
};

/**
 * Get field names for a specific day
 * @param {number} dayIndex - Index of the day (1-7)
 * @returns {Object} Object with field names for individual, group, and discharge
 */
export const getDayFieldNames = (dayIndex) => {
  return {
    individual: `i${dayIndex}TmtTmtStsId`,
    group: `g${dayIndex}TmtTmtStsId`,
    discharge: `d${dayIndex}TmtTmtStsId`,
    faceTime: `faceTimei${dayIndex}`,
    supplementalTime: `supplementalTimei${dayIndex}`,
    faceHours: `i${dayIndex}TmtFaceHours`,
    faceMins: `i${dayIndex}TmtFaceMins`,
    suppHours: `i${dayIndex}TmtSuppHours`,
    suppMins: `i${dayIndex}TmtSuppMins`,
    stopHours: `stop${dayIndex}Hours`,
  };
};

/**
 * Extract status ID from verification state by field name
 * @param {string} fieldName - Field name
 * @param {Object} verificationState - Verification state object
 * @returns {number|null} Status ID or null
 */
export const getStatusIdFromField = (fieldName, verificationState) => {
  return verificationState[fieldName] || null;
};

/**
 * Check if individual and group treatments exist on the same day
 * @param {number} dayIndex - Day index (1-7)
 * @param {Object} verificationState - Verification state object
 * @returns {boolean} True if both exist on the same day
 */
export const hasSameDayTreatment = (dayIndex, verificationState) => {
  const individualField = `i${dayIndex}TmtTmtStsId`;
  const groupField = `g${dayIndex}TmtTmtStsId`;

  return (
    verificationState[individualField] !== null &&
    verificationState[individualField] !== undefined &&
    verificationState[groupField] !== null &&
    verificationState[groupField] !== undefined
  );
};

/**
 * Validate all days for same-day treatment conflicts
 * @param {Object} verificationState - Verification state object
 * @returns {boolean} True if any day has both individual and group treatments
 */
export const hasAnySameDayTreatment = (verificationState) => {
  for (let i = 1; i <= 7; i++) {
    if (hasSameDayTreatment(i, verificationState)) {
      return true;
    }
  }
  return false;
};

/**
 * Format date for API calls
 * @param {Date} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateForApi = (date) => {
  return moment(date).format(formDateFormat);
};

/**
 * Add days to a date
 * @param {Date|string} date - Starting date
 * @param {number} days - Number of days to add
 * @returns {Date} New date
 */
export const addDays = (date, days) => {
  return calculateDayDate(date, days);
};
